import { nextTick, onBeforeUnmount, ref, watch, type Ref } from "vue";
import {
  areCatalogueTabsWrapped,
  openHeaderPopovers,
} from "@/stores/CanvasLayoutState";
import { isCanvasDrawing } from "@/stores/CanvasViewState";
import { shouldWrapCatalogueTabs } from "@/utils/catalogue-tabs";
import { shouldCompactLegend } from "@/utils/header-layout";

interface HeaderElements {
  /** L'en-tête : on y retrouve les onglets, où qu'ils soient (1re ou 2e ligne). */
  header: Ref<HTMLElement | null>;
  /** Emplacement des onglets sur la 1re ligne (vide quand ils sont en 2e). */
  tabsSlot: Ref<HTMLElement | null>;
  /** Menu fichier (logo + nom du plan). */
  fileArea: Ref<HTMLElement | null>;
  /** Copies invisibles du groupe Légende + Échelle, déplié et replié. */
  legendExpandedProbe: Ref<HTMLElement | null>;
  legendCompactProbe: Ref<HTMLElement | null>;
}

/**
 * Mise en page automatique de l'en-tête, selon la largeur disponible :
 * - la légende et l'échelle se replient en un seul bouton quand le nom du
 *   plan n'a plus la place d'être lu ;
 * - les onglets de catalogue passent sur une 2e ligne quand ils ne tiennent
 *   plus sur la 1re.
 *
 * Toutes les largeurs sont des mesures de mise en page (offsetWidth…) : même
 * unité que les seuils, quelle que soit la taille de l'interface (zoom CSS).
 *
 * Une seule décision par image, la légende d'abord : l'autre se prend sur la
 * mise en page qui en résulte (sinon l'en-tête passerait 1 → 2 → 1 ligne et
 * le plan sauterait deux fois). Les décisions s'appliquent hors du rappel du
 * ResizeObserver, et jamais pendant un tracé ni popup de l'en-tête ouverte.
 */
export function useCanvasHeaderLayout(elements: HeaderElements) {
  const isLegendCompact = ref(false);
  let frame = 0;
  let observedStrip: Element | null = null;

  const observer = new ResizeObserver(schedule);

  function schedule() {
    if (!frame) frame = requestAnimationFrame(apply);
  }

  /** Après une décision : remesurer quand Vue aura appliqué le changement. */
  function scheduleAfterRender() {
    void nextTick(schedule);
  }

  /** Les onglets changent d'élément en passant d'une ligne à l'autre. */
  function findTabsStrip(): HTMLElement | null {
    const strip =
      elements.header.value?.querySelector<HTMLElement>(".catalogue-strip") ??
      null;
    if (strip !== observedStrip) {
      if (observedStrip) observer.unobserve(observedStrip);
      if (strip) observer.observe(strip);
      observedStrip = strip;
    }
    return strip;
  }

  function apply() {
    frame = 0;
    // Pas pendant un tracé : la hauteur de l'en-tête déplace le plan sous le
    // doigt. On reprend au lever (cf. watch plus bas).
    if (isCanvasDrawing.value) return;
    const slot = elements.tabsSlot.value;
    // En-tête démonté ou masqué (plein écran) : rien à mesurer.
    if (!slot || slot.getClientRects().length === 0) return;

    // 1. Légende repliée ? Figée tant qu'une de ses popups est ouverte.
    const file = elements.fileArea.value;
    const expanded = elements.legendExpandedProbe.value;
    const compactProbe = elements.legendCompactProbe.value;
    if (openHeaderPopovers.value === 0 && file && expanded && compactProbe) {
      const name = file.querySelector<HTMLElement>(".file-name");
      const compact = shouldCompactLegend({
        compact: isLegendCompact.value,
        fileWidth: file.offsetWidth,
        fileOverflow: name
          ? Math.max(0, name.scrollWidth - name.clientWidth)
          : 0,
        freeWidth: slot.offsetWidth,
        expandDelta: expanded.offsetWidth - compactProbe.offsetWidth,
      });
      if (compact !== isLegendCompact.value) {
        isLegendCompact.value = compact;
        scheduleAfterRender();
        return;
      }
    }

    // 2. Onglets en 2e ligne ?
    const strip = findTabsStrip();
    const wrapped = shouldWrapCatalogueTabs(
      strip ? strip.offsetWidth : null,
      slot.offsetWidth,
      areCatalogueTabsWrapped.value,
    );
    if (wrapped !== areCatalogueTabsWrapped.value) {
      areCatalogueTabsWrapped.value = wrapped;
      scheduleAfterRender();
    }
  }

  watch(
    [
      elements.tabsSlot,
      elements.fileArea,
      elements.legendExpandedProbe,
      elements.legendCompactProbe,
    ],
    (els) => {
      observer.disconnect();
      observedStrip = null;
      for (const el of els) if (el) observer.observe(el);
      schedule();
    },
    { flush: "post", immediate: true },
  );

  // Reprise après un tracé, ou à la fermeture des popups de l'en-tête.
  watch(isCanvasDrawing, (drawing) => {
    if (!drawing) schedule();
  });
  watch(openHeaderPopovers, (count) => {
    if (count === 0) schedule();
  });

  onBeforeUnmount(() => {
    observer.disconnect();
    if (frame) cancelAnimationFrame(frame);
  });

  return { isLegendCompact };
}
